import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const TestimonialsPage = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const testimonials = [
    {
      name: "Amara Solis",
      role: "Podcast Producer",
      company: "The Daily Brief",
      text: "SoundFlowAI cut my editing time by 75%. The noise reduction alone is worth the subscription. I used to spend 4 hours per episode—now it's under an hour.",
      rating: 5
    },
    {
      name: "Eamon Rix",
      role: "Music Producer",
      company: "Beats & Beyond Studio",
      text: "Finally, an AI tool that understands music. The stem separation is incredible, and the mixing suggestions are spot-on. It's like having a pro engineer on call 24/7.",
      rating: 5
    },
    {
      name: "Tavia Quinn",
      role: "Sound Designer",
      company: "GameAudio Labs",
      text: "I've tried every audio tool out there. Nothing comes close to the workflow automation this platform offers. Generated 200+ sound effects in a single afternoon.",
      rating: 5
    },
    {
      name: "Kael Dorian",
      role: "Content Creator",
      company: "2.5M YouTube Subscribers",
      text: "My audience immediately noticed the audio quality improvement. The auto-ducking and voice enhancement features are game-changers for video creators.",
      rating: 5
    },
    {
      name: "Selena Lane",
      role: "Audio Engineer",
      company: "Broadcast Media Inc",
      text: "We process thousands of audio files weekly. The batch processing and API integration saved our team countless hours. ROI was visible within the first month.",
      rating: 5
    },
  ];

  return (
    <div className="min-h-screen bg-dark-base pt-24">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-dark-base/95 backdrop-blur border-b border-neon-orange/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-white font-heading font-bold text-xl hover:text-neon-orange transition-colors">
            ← Back to Home
          </Link>
          <h1 className="text-neon-orange font-heading font-bold">Customer Reviews</h1>
          <div className="w-24"></div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Loved by <span className="text-neon-orange">Audio Professionals</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            See what creators and studios are saying about VeritoneTech AI.
          </p>
        </motion.div>

        {/* Featured Testimonial */}
        <motion.div
          key={selectedIndex}
          className="mb-16 p-8 md:p-12 bg-gradient-to-br from-neon-orange/10 to-dark-deeper rounded-2xl border-2 border-neon-orange"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          {/* Rating */}
          <div className="flex gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <motion.svg
                key={i}
                className="w-6 h-6 text-neon-orange"
                fill="currentColor"
                viewBox="0 0 24 24"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ delay: i * 0.05, duration: 0.3 }}
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </motion.svg>
            ))}
          </div>

          {/* Quote */}
          <blockquote className="text-xl md:text-2xl text-white font-light mb-8 leading-relaxed">
            "{testimonials[selectedIndex].text}"
          </blockquote>

          {/* Author */}
          <div>
            <div className="font-semibold text-white text-lg">{testimonials[selectedIndex].name}</div>
            <div className="text-neon-orange text-sm">{testimonials[selectedIndex].role}</div>
            <div className="text-gray-500 text-sm">{testimonials[selectedIndex].company}</div>
          </div>
        </motion.div>

        {/* Testimonial Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {testimonials.map((testimonial, i) => (
            <motion.div
              key={i}
              className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                selectedIndex === i
                  ? 'border-neon-orange bg-neon-orange/5'
                  : 'border-neon-orange/20 bg-dark-deeper/50 hover:border-neon-orange/40'
              }`}
              onClick={() => setSelectedIndex(i)}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-white">{testimonial.name}</h3>
                  <p className="text-neon-orange text-sm">{testimonial.role}</p>
                  <p className="text-gray-500 text-xs">{testimonial.company}</p>
                </div>
                <div className="flex gap-0.5">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <svg
                      key={j}
                      className="w-4 h-4 text-neon-orange"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
              </div>
              <p className="text-gray-300 text-sm mt-4 line-clamp-2">{testimonial.text}</p>
            </motion.div>
          ))}
        </div>

        {/* Stats */}
        <motion.div
          className="grid md:grid-cols-3 gap-8 py-12 border-t border-b border-neon-orange/10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {[
            { label: "Satisfied Customers", value: "5000+" },
            { label: "Average Rating", value: "4.9★" },
            { label: "Time Saved (hrs/year)", value: "500K+" },
          ].map((stat, i) => (
            <motion.div
              key={i}
              className="text-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.1 }}
            >
              <div className="text-3xl font-bold text-neon-orange mb-2">{stat.value}</div>
              <p className="text-gray-400 text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-gray-400 mb-6">Join thousands of creators using VeritoneTech AI</p>
          <Link 
            to="/veritonetech-ai"
            className="inline-block px-8 py-4 bg-neon-orange text-dark-base font-bold rounded-full hover:shadow-lg hover:shadow-neon-orange/50 transition-all"
          >
            Start Your Free Trial
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default TestimonialsPage;
